import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import Select, {components} from "react-select";
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';

/** Safe DOM id segment for react-select `instanceId` (listbox id = react-select-{id}-listbox). */
function sanitizeReactSelectInstanceId(value) {
  if (value == null || String(value).trim() === '') return null;
  return String(value).replace(/[^a-zA-Z0-9_-]/g, '_');
}


const DefaultDropdownIndicator = props => {
    return (
      <components.DropdownIndicator {...props}>
        <ArrowDropDownIcon/>
      </components.DropdownIndicator>
    );
  };

const ReactSelectMultiSelect = (props) => {
    const [isClearable, setIsClearable] = useState(true);
    const selectInstanceRef = useRef(null);
    const boundaryRef = useRef(null);
    const fallbackInstanceIdRef = useRef(null);
    const { 
      name, 
      eleRef, 
      className, 
      options, 
      ValueContainer, 
      styles, 
      onChange, 
      value, 
      isMulti, 
      isClearableCheck, 
      placeholder, 
      defaultValue, 
      components: customComponents, 
      isDisabled, 
      classNamePrefix = 'prov', 
      error,
      instanceId: instanceIdProp,
    } = props

    const instanceId = useMemo(() => {
      if (instanceIdProp != null && String(instanceIdProp).trim() !== '') {
        const s = sanitizeReactSelectInstanceId(instanceIdProp);
        return s || `id_${String(instanceIdProp).replace(/\W/g, '').slice(0, 24) || 'x'}`;
      }
      const fromName = sanitizeReactSelectInstanceId(name);
      if (fromName) return fromName;
      if (!fallbackInstanceIdRef.current) {
        fallbackInstanceIdRef.current = `rs_${Math.random().toString(36).slice(2, 11)}`;
      }
      return fallbackInstanceIdRef.current;
    }, [name, instanceIdProp]);

    const setSelectRef = useCallback(
      (instance) => {
        selectInstanceRef.current = instance;
        if (typeof eleRef === 'function') {
          eleRef(instance);
        } else if (eleRef && typeof eleRef === 'object') {
          eleRef.current = instance;
        }
      },
      [eleRef]
    );

    /**
     * Multi + portaled menu: blur alone is unreliable. Call the same API as the chevron:
     * `onMenuClose()`. Scope "inside" to this instance only: wrapper contains the control; the
     * listbox is portaled — use `react-select-{instanceId}-listbox`. Do not use `[class*="prov__"]`
     * alone or clicks on another filter's control would wrongly count as "inside".
     */
    useEffect(() => {
      const listboxId = `react-select-${instanceId}-listbox`;
      const onDocMouseDown = (event) => {
        const t = event.target;
        if (!(t instanceof Element)) return;
        if (boundaryRef.current?.contains(t)) return;
        if (document.getElementById(listboxId)?.contains(t)) return;
        const inst = selectInstanceRef.current;
        if (!inst) return;
        if (typeof inst.onMenuClose === 'function') {
          inst.onMenuClose();
        } else if (typeof inst.blur === 'function') {
          inst.blur();
        }
      };
      document.addEventListener('mousedown', onDocMouseDown);
      return () => document.removeEventListener('mousedown', onDocMouseDown);
    }, [instanceId]);

    const mergedComponents = {
        DropdownIndicator: DefaultDropdownIndicator,
        ...(customComponents || {}),
        ...(ValueContainer ? { ValueContainer } : {}),
    };

    // merge user styles with an override for the control when `error` is true
    const mergedStyles = {
      ...(styles || {}),
      control: (provided, state) => {
        // allow user's control style to be a function or an object
        const userControl = styles && styles.control;
        const base = typeof userControl === 'function'
          ? userControl(provided, state)
          : { ...provided, ...(userControl || {}) };

        if (!error) {
          return base;
        }

        // error visual (adjust colors as needed)
        const errorBorder = {
          // ensure a visible border by default
          border: '1px solid #d32f2f',
          borderColor: '#d32f2f',
          // show subtle focus ring when focused, otherwise keep no shadow
          boxShadow: state.isFocused ? '0 0 0 1px rgba(211,47,47,0.25)' : 'none',
          '&:hover': {
            borderColor: '#d32f2f',
          }
        };

        return { ...base, ...errorBorder };
      }
    };

    return (
        <div ref={boundaryRef} className="react-select-multi-boundary min-w-0 w-full">
        <Select
            menuPortalTarget={document.body} menuPosition={'fixed'}
            instanceId={instanceId}
            name={name}
            ref={setSelectRef}
            className={className}
            classNamePrefix={classNamePrefix}
            options={options}
            isMulti={isMulti}
            isClearable={isClearableCheck ? isClearable : false}
            isDisabled={isDisabled}
            hideSelectedOptions={false}
            components={mergedComponents}
            styles={mergedStyles}
            placeholder={placeholder}
            isSearchable={true}
            onChange={onChange}
            value= {value}
            defaultValue={defaultValue}
            closeMenuOnSelect={!isMulti}
        />
        </div>
    );
};

export default ReactSelectMultiSelect;
